import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from "./user.service";
import { LogoutRequestData } from "../models/user.model";
import { SKIP_AUTH } from "./auth-context.token";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _authService: AuthService = inject(AuthService);
  private _userService: UserService = inject(UserService);

  constructor() {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const shouldSkip = req.context.get(SKIP_AUTH);

    if (shouldSkip || req.url.includes('/auth/token/refresh/')) {
      return next.handle(req);
    }

    const accessToken = this._authService.getAccessToken();
    const authReq = accessToken
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && error.error?.code === 'token_not_valid') {
          return this._authService.refreshToken().pipe(
            switchMap((response: { access: string }) => {
              this._authService.setAccessToken(response.access);
              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${response.access}` },
              });
              return next.handle(retryReq);
            }),
            catchError(refreshError => throwError(refreshError))
          );
        }

        const detail = error?.error?.detail as string;

        if (detail === 'Authentication credentials were not provided.') {
          this._userService.delete();
          this._authService.clearAccessToken();
          return next.handle(req);
        }

        if (typeof detail === 'string' && detail.startsWith('Request was throttled')) {
          const secondsMatch = detail.match(/\d+/);
          const seconds = secondsMatch ? parseInt(secondsMatch[0], 10) : 0;

          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          const formatted = `${(hours < 10 ? '0' : '') + hours.toString()}:${(minutes < 10 ? '0' : '') + minutes.toString()}`;

          const customError = new HttpErrorResponse({
            error: { message: `Sorry, try again after ${formatted}.` },
            status: error.status,
            statusText: error.statusText,
            headers: error.headers,
            url: error.url ?? undefined, // fix for null compatibility
          });

          return throwError(customError);
        }

        return throwError(error);
      })
    );
  }
}

